import React, { ReactElement, ReactNode, useEffect, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import AuthSteps from './components/AuthSteps';
import tracking from '../../utils/tracking';
import { AuthStateContext, authMachine } from '../../utils/authFormState';
import { AuthStepsProps } from './components/AuthSteps/AuthSteps';
import { PrivateRoutePath } from '../../utils/routes';
import { SignInSource, TrackingEvent } from '../../utils/tracking/types';
import { authenticated$ } from '../../store/session/selectors';

export interface AuthProps extends AuthStepsProps {
  source: SignInSource;
  children?: ReactNode;
}

/**
 * Authenticate mobile number context provider, can be used as a standalone component or as part of a parent form.
 *
 * @param AuthProps - Tracking option and optional redirect after authentication.
 */
function Auth({ children, source, ...props }: AuthProps): ReactElement {
  const { push } = useRouter();
  const [current, send] = useMachine(authMachine, { devTools: true });
  const authenticated = useSelector(authenticated$);

  useEffect(() => {
    if (authenticated) {
      tracking.event(TrackingEvent.SIGN_IN, { source });
      void push(PrivateRoutePath.PROFILE);
    }
  }, [authenticated, push, source]);

  const contextValue = useMemo(() => ({ current, send }), [current, send]);

  return (
    <AuthStateContext.Provider value={contextValue}>{children ?? <AuthSteps {...props} />}</AuthStateContext.Provider>
  );
}

export default Auth;
